/**
 * CARIBE7 MODULES
 */
import Emitter       from '@shared/services/main/events';
import RequestClient from '@shared/services/main/request-client';
import EVENT_NAMES   from '@shared/lib/constants/events';

/**
 * This prototype describes an session token trait. Encapsulate business logics
 *
 * @class      SessionTokenTrait (name)
 */
function SessionTokenTrait(component) {
  this.component   = component;
  this.event_group = EVENT_NAMES.PLATFORM.SESSIONS;
}

/**
 * Registers Read events, this follows naming convention
 *
 * Let us allow this to accept a callback so we can override success
 * destination/logic
 *
 * @param      {Object}        self     The caller instance
 * @param      {SessionTokenTrait}  service  The service
 * @param      {string}  module_name  The module name
 * @return     {Object}        { description_of_the_return_value }
 */
SessionTokenTrait.prototype.registerSessionTokenEvents = function(self, service) {
  var event_callback_map = {};
  event_callback_map[this.event_group.TOKEN_PROCEED] = 'onSessionTokenProceed';
  event_callback_map[this.event_group.TOKEN_SUCCESS] = 'onSessionTokenSuccess';
  event_callback_map[this.event_group.TOKEN_FAIL]    = 'onSessionTokenFail';
  this.registerEvents(event_callback_map, service);
  Emitter.register(
    EVENT_NAMES.PLATFORM.SESSIONS.START_SUCCESS_AFTER + '-token-service',
    this.onSessionTokenProceed.bind(this)
  );
  return this;
}

/**
 * Removes Read events.
 *
 * @param      {<type>}  self         The object
 * @param      {<type>}  service      The service
 * @param      {string}  module_name  The module name
 */
SessionTokenTrait.prototype.removeSessionTokenEvents = function() {
  this.removeEvents([
    this.event_group.TOKEN_PROCEED,
    this.event_group.TOKEN_SUCCESS,
    this.event_group.TOKEN_FAIL
  ]);
  Emitter.off(EVENT_NAMES.PLATFORM.SESSIONS.START_SUCCESS_AFTER + '-token-service');
  return this;
}

/**
 * Gives the request extra options,
 *
 * NOTE: this will override the same option keys
 *
 * @return     {Object}  The request extra options
 */
SessionTokenTrait.prototype.extraTokenReadOptions = function() {
  return {
    headers: {
      'Access-Control-Expose-Headers': 'SA-TOKEN'
    }
  };
}

/**
 * Called when read proc
 *
 * @param      {Object}  arg1         The argument 1
 * @param      {String}  arg1.url     The url
 * @param      {Object}  arg1.params  The parameters
 */
SessionTokenTrait.prototype.onSessionTokenProceed = function() {
  // Only proceed if user has prepaid data in context
  // This prevents 401 errors for non-prepaid users
  // The token is only needed for prepaid betting and virtual sports operations
  const component = this.component;
  const context = component?.context;
  
  if (context) {
    const [{ player, wallet, prepaid_player_id, prepaid_wallet_id }] = context;
    
    // Check if user has prepaid data in context
    // Prepaid data may come from:
    // 1. Previous prepaid session load (refresh scenario)
    // 2. Login response session data (if included)
    const hasPrepaidInContext = prepaid_player_id || prepaid_wallet_id || 
                                 player?.prepaid_player_id || wallet?.prepaid_wallet_id;
    
    // If no prepaid data in context, skip the call
    // This prevents unnecessary 401 errors for regular (non-prepaid) users
    // Prepaid users will have their data loaded through other mechanisms
    // or can trigger it manually when needed
    if (!hasPrepaidInContext) {
      return;
    }
  }
  
  let token_source = this.renewCancelToken(RequestClient, this.event_group.TOKEN_PROCEED);
  const options = this.extraTokenReadOptions();
  let request = RequestClient.post(this.token_read_endpoint, {}, {
    cancelToken: token_source.token,
    ...options
  });
  this.bindResultEvents(
    request,
    this.event_group.TOKEN_SUCCESS,
    this.event_group.TOKEN_FAIL
  );
}

/**
 * Called on read success.
 *
 * @param      {Object}  response  The response
 */
SessionTokenTrait.prototype.onSessionTokenSuccess = function(response) {
  /**
   * ALso emit other extensions
   */
  const sa_token = response.headers['x-sa-token'];
  Emitter.emit(EVENT_NAMES.PLATFORM.SESSIONS.TOKEN_SUCCESS_AFTER, {
    sa_token: sa_token
  });
}

/**
 * Called on read fail.
 *
 * @param      {Object}  error   The error
 */
SessionTokenTrait.prototype.onSessionTokenFail = function() {
}

export default SessionTokenTrait;
